import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle, Loader2, RefreshCw, Search, Sparkles, X } from "lucide-react";
import { useMemo, useState } from "react";

import { cn } from "@/lib/cn";
import { haptic } from "@/lib/telegram";
import {
  useEmojiLibrary,
  type EmojiAsset,
  type EmojiItem,
  type EmojiPack,
} from "@/api/emoji";

export interface PickedEmoji {
  unicode: string;
  document_id: string;
  is_free: boolean;
  asset_url?: string | null;
  set_id: string;
  set_title: string;
}

interface PremiumEmojiPickerProps {
  open: boolean;
  onClose: () => void;
  onPick: (emoji: PickedEmoji) => void;
}

export function PremiumEmojiPicker({
  open,
  onClose,
  onPick,
}: PremiumEmojiPickerProps) {
  const [activeSetId, setActiveSetId] = useState<string | null>(null);
  const [query, setQuery] = useState("");

  const {
    data: libraryData,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useEmojiLibrary({ enabled: open });

  const libraryPack = useMemo<EmojiPack | null>(() => {
    const emojis = libraryData?.emojis ?? [];
    if (emojis.length === 0) return null;
    return {
      set_id: "__bot_library__",
      access_hash: "0",
      short_name: "bot-library",
      title: "Из бота",
      installed: true,
      emojis: emojis.map((e) => ({
        document_id: e.document_id,
        unicode: e.unicode || "✨",
        keywords: ["bot", "из бота"],
        is_free: e.is_free,
      })),
    };
  }, [libraryData]);

  const packs = useMemo(() => (libraryPack ? [libraryPack] : []), [libraryPack]);
  const isBodyLoading = isLoading && packs.length === 0;
  const fatalError = packs.length === 0 ? error : null;

  const effectiveSetId =
    activeSetId ??
    packs.find((p) => p.installed)?.set_id ??
    packs[0]?.set_id ??
    null;

  const visiblePacks = useMemo(() => {
    if (!query) return packs;
    const q = query.toLowerCase();
    return packs
      .map((p) => ({
        ...p,
        emojis: p.emojis.filter(
          (e) =>
            e.unicode.includes(query) ||
            e.keywords.some((k) => k.toLowerCase().includes(q)),
        ),
      }))
      .filter((p) => p.emojis.length > 0);
  }, [packs, query]);

  const activePack: EmojiPack | undefined = query
    ? undefined
    : packs.find((p) => p.set_id === effectiveSetId) ?? packs[0];

  const assetMap = useMemo(() => {
    const out: Record<string, EmojiAsset> = {};
    for (const emoji of libraryData?.emojis ?? []) {
      if (!emoji.image_url) continue;
      out[emoji.document_id] = {
        document_id: emoji.document_id,
        unicode: emoji.unicode,
        is_free: emoji.is_free,
        image_url: emoji.image_url,
        mime_type: emoji.mime_type,
      };
    }
    return out;
  }, [libraryData]);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            onClick={onClose}
          />
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", stiffness: 360, damping: 32 }}
            className={cn(
              "fixed inset-x-0 bottom-0 z-[70] glass-strong rounded-t-[24px]",
              "border-t border-white/[0.08] max-h-[70dvh] flex flex-col",
            )}
          >
            <div className="flex items-center justify-center pt-3 pb-1 shrink-0">
              <div className="w-10 h-1 rounded-full bg-white/15" />
            </div>

            <div className="px-5 pt-2 pb-3 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2 min-w-0">
                <Sparkles className="size-4 text-accent shrink-0" />
                <div className="text-base font-semibold truncate">
                  Премиум-эмодзи
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => {
                    haptic.select();
                    void refetch();
                  }}
                  disabled={isFetching}
                  aria-label="Обновить"
                  className="size-8 grid place-items-center rounded-full bg-white/[0.06] hover:bg-white/[0.1] transition-colors disabled:opacity-50"
                >
                  <RefreshCw
                    className={cn(
                      "size-3.5",
                      isFetching && "animate-spin",
                    )}
                  />
                </button>
                <button
                  onClick={onClose}
                  aria-label="Закрыть"
                  className="size-8 grid place-items-center rounded-full bg-white/[0.06] hover:bg-white/[0.1] transition-colors"
                >
                  <X className="size-4" />
                </button>
              </div>
            </div>

            <div className="px-5 pb-2 shrink-0">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-text-dim" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Поиск"
                  className={cn(
                    "w-full h-10 pl-9 pr-3 rounded-xl",
                    "bg-surface border border-white/[0.06]",
                    "text-sm outline-none focus:border-accent/40 transition-colors",
                  )}
                />
              </div>
            </div>

            {!query && packs.length > 0 && (
              <div className="px-5 pb-2 shrink-0 flex gap-1.5 overflow-x-auto no-scrollbar">
                {packs.map((p) => (
                  <PackTab
                    key={p.set_id}
                    pack={p}
                    active={p.set_id === effectiveSetId}
                    onClick={() => {
                      setActiveSetId(p.set_id);
                      haptic.select();
                    }}
                  />
                ))}
              </div>
            )}

            <div className="flex-1 overflow-y-auto px-3 pb-6 min-h-[200px]">
              {isBodyLoading && <LoadingState />}
              {!isBodyLoading && fatalError && (
                <ErrorState
                  error={fatalError}
                  onRetry={() => {
                    void refetch();
                  }}
                />
              )}
              {!isBodyLoading && !fatalError && (
                <Content
                  query={query}
                  visiblePacks={visiblePacks}
                  activePack={activePack}
                  assets={assetMap}
                  onPick={(e, pack) => {
                    haptic.impact("light");
                    const asset = assetMap[e.document_id];
                    onPick({
                      unicode: e.unicode,
                      document_id: e.document_id,
                      is_free: e.is_free,
                      asset_url: asset?.image_url ?? null,
                      set_id: pack.set_id,
                      set_title: pack.title,
                    });
                  }}
                />
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function PackTab({
  pack,
  active,
  onClick,
}: {
  pack: EmojiPack;
  active: boolean;
  onClick: () => void;
}) {
  const sample = pack.emojis[0]?.unicode ?? "✨";
  return (
    <button
      onClick={onClick}
      className={cn(
        "px-3 py-1.5 rounded-full text-xs font-medium shrink-0 transition-colors",
        "flex items-center gap-1.5 max-w-[180px]",
        active
          ? "bg-accent text-white"
          : "bg-white/[0.04] text-text-muted hover:bg-white/[0.08]",
      )}
      title={pack.title}
    >
      <span className="text-sm leading-none">{sample}</span>
      <span className="truncate">{pack.title || pack.short_name || "Set"}</span>
    </button>
  );
}

function Content({
  query,
  visiblePacks,
  activePack,
  assets,
  onPick,
}: {
  query: string;
  visiblePacks: EmojiPack[];
  activePack?: EmojiPack;
  assets: Record<string, EmojiAsset>;
  onPick: (e: EmojiItem, p: EmojiPack) => void;
}) {
  if (query) {
    if (visiblePacks.length === 0) {
      return (
        <div className="text-center text-sm text-text-dim py-10">
          Ничего не найдено
        </div>
      );
    }
    return (
      <div className="space-y-4 pt-1">
        {visiblePacks.map((p) => (
          <PackSection
            key={p.set_id}
            pack={p}
            assets={assets}
            onPick={onPick}
            showTitle
          />
        ))}
      </div>
    );
  }

  if (!activePack) {
    return (
      <div className="text-center text-sm text-text-dim py-10">
        Нет сохранённых премиум-эмодзи
      </div>
    );
  }

  return (
    <PackSection
      pack={activePack}
      assets={assets}
      onPick={onPick}
    />
  );
}

function PackSection({
  pack,
  assets,
  onPick,
  showTitle,
}: {
  pack: EmojiPack;
  assets: Record<string, EmojiAsset>;
  onPick: (e: EmojiItem, p: EmojiPack) => void;
  showTitle?: boolean;
}) {
  return (
    <div>
      {showTitle && (
        <div className="px-1 pb-1.5 text-[10px] uppercase tracking-wider text-text-dim font-medium">
          {pack.title || pack.short_name}
        </div>
      )}
      <div className="grid grid-cols-6 gap-1">
        {pack.emojis.map((e) => {
          const asset = assets[e.document_id];
          return (
            <button
              key={e.document_id}
              onClick={() => onPick(e, pack)}
              className={cn(
                "aspect-square rounded-xl text-2xl relative",
                "hover:bg-white/[0.06] active:scale-90 transition-all",
                "grid place-items-center",
              )}
              title={e.keywords.length ? e.keywords.join(", ") : e.unicode}
            >
              {asset?.image_url ? (
                <img
                  src={asset.image_url}
                  alt={e.unicode}
                  className="size-8 object-contain"
                  loading="lazy"
                />
              ) : (
                e.unicode
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="grid grid-cols-6 gap-1 pt-1">
      {Array.from({ length: 24 }).map((_, i) => (
        <div
          key={i}
          className="aspect-square rounded-xl bg-white/[0.03] animate-pulse"
        />
      ))}
    </div>
  );
}

function ErrorState({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  return (
    <div className="py-10 px-4 flex flex-col items-center text-center max-w-sm mx-auto">
      <div className="size-12 rounded-2xl bg-accent-soft text-accent grid place-items-center mb-3">
        <AlertTriangle className="size-5" />
      </div>
      <div className="text-sm font-semibold mb-1">
        Не удалось загрузить эмодзи
      </div>
      <div className="text-xs text-text-muted mb-4">
        {(error as Error)?.message || "Попробуйте ещё раз"}
      </div>
      <button
        onClick={onRetry}
        className="text-xs px-3 py-1.5 rounded-full bg-white/[0.06] hover:bg-white/[0.1] flex items-center gap-1.5 transition-colors"
      >
        <Loader2 className="size-3" />
        Повторить
      </button>
    </div>
  );
}
